import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Tabs } from '~/components/ui/Tabs';
import { PerspectiveCaption } from '~/components/ui/PerspectiveCaption';
import { Switch } from '~/components/ui/inputs/Switch';
import {
  useBuyerPriceNegotiations,
  useBuyerPriceNegotiationCounts,
  useSellerPriceNegotiations,
  useSellerPriceNegotiationCounts,
  type BuyerPriceNegotiationFilters,
  type SellerPriceNegotiationFilters,
} from '~/api';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { PriceNegotiationStatus } from '~/graphql/graphql';
import type {
  BuyerPriceNegotiationsQuery,
  SellerPriceNegotiationsQuery,
  TradePerspective,
} from '~/graphql/graphql';
import { PriceNegotiationList } from '~/pages/price-negotiations/sections/PriceNegotiationList';
import type { PriceNegotiationRow } from '~/pages/price-negotiations/priceNegotiationProduct';

interface PriceNegotiationsProps {
  perspective: TradePerspective;
}

function statusesForTab(tab: string): PriceNegotiationStatus[] {
  if (tab === 'responded') {
    return [PriceNegotiationStatus.Accepted, PriceNegotiationStatus.Declined];
  }
  return [PriceNegotiationStatus.Pending];
}

export default function PriceNegotiations({
  perspective,
}: Readonly<PriceNegotiationsProps>) {
  const { t } = useTranslation('price-negotiations');
  const currentTab = useLocation().pathname.split('/')[2] || '';
  const [assignedToMe, setAssignedToMe] = useState(false);

  const isSelling = perspective === 'SELLING';
  const onlyAssignedToMe = isSelling && assignedToMe;
  const status = useMemo(() => statusesForTab(currentTab), [currentTab]);

  const sellerFilters = useMemo<SellerPriceNegotiationFilters>(
    () => ({ assignedToMe: onlyAssignedToMe, status }),
    [onlyAssignedToMe, status]
  );
  const buyerFilters = useMemo<BuyerPriceNegotiationFilters>(
    () => ({ status }),
    [status]
  );

  const sellerList = usePaginatedQuery<
    SellerPriceNegotiationsQuery,
    PriceNegotiationRow,
    SellerPriceNegotiationFilters
  >({
    fetchHook: useSellerPriceNegotiations,
    select: (data) => data?.sellerPriceNegotiations,
    options: { filters: sellerFilters, enabled: isSelling },
  });
  const buyerList = usePaginatedQuery<
    BuyerPriceNegotiationsQuery,
    PriceNegotiationRow,
    BuyerPriceNegotiationFilters
  >({
    fetchHook: useBuyerPriceNegotiations,
    select: (data) => data?.buyerPriceNegotiations,
    options: { filters: buyerFilters, enabled: !isSelling },
  });
  const list = isSelling ? sellerList : buyerList;

  const { data: sellerCountsData } = useSellerPriceNegotiationCounts(
    onlyAssignedToMe,
    { enabled: isSelling }
  );
  const { data: buyerCountsData } = useBuyerPriceNegotiationCounts({
    enabled: !isSelling,
  });
  const counts = isSelling
    ? sellerCountsData?.sellerPriceNegotiationCounts
    : buyerCountsData?.buyerPriceNegotiationCounts;

  const listFor = (emptyMessage: string, isRespondedTab: boolean) => (
    <PriceNegotiationList
      perspective={perspective}
      negotiations={list.data}
      isLoading={list.isLoading}
      isError={Boolean(list.error)}
      emptyMessage={emptyMessage}
      sentinelRef={list.sentinelRef}
      isLoadingMore={list.isLoadingMore}
      hasMoreData={list.hasMoreData}
      totalCount={list.pagination?.totalElements ?? 0}
      showResponseColumns={isRespondedTab}
      showRespondAction={isSelling && !isRespondedTab}
      showBuildOrderAction={!isSelling && isRespondedTab}
      onResponded={list.removeItem}
    />
  );

  const audience = isSelling ? 'seller' : 'buyer';
  const tabs = [
    {
      label: t(`price_negotiations.tabs.${audience}.waiting`),
      count: counts?.waiting ?? 0,
      link: 'waiting',
      component: listFor(
        t(`price_negotiations.${audience}.no_waiting_message`),
        false
      ),
    },
    {
      label: t(`price_negotiations.tabs.${audience}.responded`),
      count: counts?.responded ?? 0,
      link: 'responded',
      component: listFor(
        t(`price_negotiations.${audience}.no_responded_message`),
        true
      ),
    },
  ];

  return (
    <div className="w-full flex flex-col gap-8">
      <title>{t('price_negotiations.title')}</title>
      <Tabs
        title={t('price_negotiations.title')}
        subtitle={<PerspectiveCaption perspective={perspective} />}
        tabs={tabs}
        basePath={`${audience}-price-negotiations`}
        headerActions={
          isSelling ? (
            <Switch
              checked={assignedToMe}
              onChange={setAssignedToMe}
              label={t('price_negotiations.assigned_to_me')}
              hint={t('price_negotiations.assigned_to_me_hint')}
              dataTestId="price-negotiations-assigned-to-me"
            />
          ) : undefined
        }
      />
    </div>
  );
}
